/** Klient API: koperta {ok,data}/{ok:false,error}; błąd = wyjątek ApiError z kodem z katalogu. */

/** Identyfikator UUID (filtry w adresie: produkt, rodzina). */
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly requestId?: string,
    readonly details?: Array<{ path?: string; message: string }>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Tekst błędu dla człowieka: komunikat API plus `details[].message` (walidacja per pole). */
export function apiErrorText(e: unknown): string {
  if (e instanceof ApiError)
    return e.details?.length
      ? `${e.message}: ${e.details.map((d) => d.message).join('; ')}`
      : e.message;
  return e instanceof Error ? e.message : String(e);
}

type Envelope<T> =
  | { ok: true; data: T }
  | {
      ok: false;
      error: {
        code: string;
        message: string;
        requestId: string;
        details?: Array<{ path?: string; message: string }>;
      };
    };

export interface RequestOpts {
  signal?: AbortSignal;
}

async function requestWithStatus<T>(
  method: string,
  url: string,
  body?: unknown,
  opts: RequestOpts = {},
): Promise<{ data: T; status: number }> {
  const init: RequestInit = { method, credentials: 'same-origin' };
  if (opts.signal) init.signal = opts.signal;
  if (body instanceof FormData) {
    init.body = body; // multipart — przeglądarka sama ustawia content-type z boundary
  } else if (body !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(url, init);
  const env = (await res.json().catch(() => null)) as Envelope<T> | null;
  if (!env) throw new ApiError('internal', `Nieczytelna odpowiedź (${res.status})`, res.status);
  if (!env.ok)
    throw new ApiError(
      env.error.code,
      env.error.message,
      res.status,
      env.error.requestId,
      env.error.details,
    );
  return { data: env.data, status: res.status };
}

async function request<T>(
  method: string,
  url: string,
  body?: unknown,
  opts: RequestOpts = {},
): Promise<T> {
  return (await requestWithStatus<T>(method, url, body, opts)).data;
}

export const api = {
  get: <T>(url: string, opts?: RequestOpts) => request<T>('GET', url, undefined, opts),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body),
  patch: <T>(url: string, body?: unknown) => request<T>('PATCH', url, body),
  delete: <T>(url: string) => request<T>('DELETE', url),
  /** Upload multipart (pole `file`); ta sama koperta i `ApiError` co reszta. */
  upload: <T>(url: string, form: FormData) => request<T>('POST', url, form),
  /** Jak `upload`, ale ze statusem HTTP (200 = już istniał, 201 = nowy). */
  uploadWithStatus: <T>(url: string, form: FormData) => requestWithStatus<T>('POST', url, form),
};
